import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Autocomplete,
  Typography,
  Button,
} from "@mui/material";
import Alert from "@mui/material/Alert"; // Import Alert for Snackbar
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, partnerDetail, onRecordUpdated }) => {
  const { updateData, deleteData } = useFirestore("TpPartners"); // Specify collection
  const { data: sitesData, loading: sitesLoading } = useFirestore(
    "Sites",
    "SiteName",
    "asc",
  );
  const [formData, setFormData] = useState({
    id: "", // To store Firestore document ID
    PartnerIRN: "",
    Tech_Prep_Partner: "",
    Partner_Full_Name: "",
    Active_yn: "n",
    Sites: [], // Array of { SiteID, SiteName } tied to this partner
  });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (partnerDetail) {
      const activeValue = String(partnerDetail.Active_yn || "")
        .trim()
        .toLowerCase();
      setFormData({
        id: partnerDetail.id || "",
        PartnerIRN: partnerDetail.PartnerIRN || "",
        Tech_Prep_Partner: partnerDetail.Tech_Prep_Partner || "",
        Partner_Full_Name: partnerDetail.Partner_Full_Name || "",
        Active_yn: activeValue === "y" || activeValue === "yes" ? "y" : "n",
        Sites: Array.isArray(partnerDetail.Sites) ? partnerDetail.Sites : [],
      });
    } else {
      // Reset form if partnerDetail is null
      setFormData({
        id: "",
        PartnerIRN: "",
        Tech_Prep_Partner: "",
        Partner_Full_Name: "",
        Active_yn: "n",
        Sites: [],
      });
    }
  }, [partnerDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? "y" : "n",
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing for update.");
      }
      await updateData(formData.id, formData); // Pass ID and data separately
      setLoading(false);
      setSnackbarMessage("Record has been updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close dialog on success
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!partnerDetail || !partnerDetail.id) {
        throw new Error("Document ID is missing for delete.");
      }
      await deleteData(partnerDetail.id);
      setLoading(false);
      setSnackbarMessage("Record has been deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close the main dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Partner
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active_yn === "y"}
                    onChange={handleCheckboxChange}
                    name="Active_yn"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Partner IRN"
                name="PartnerIRN"
                value={formData.PartnerIRN}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tech Prep Partner"
                name="Tech_Prep_Partner"
                value={formData.Tech_Prep_Partner}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                name="Partner_Full_Name"
                value={formData.Partner_Full_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={sitesData || []}
                loading={sitesLoading}
                getOptionLabel={(option) => option.SiteName || ""}
                isOptionEqualToValue={(option, value) =>
                  option.SiteID === value.SiteID
                }
                value={formData.Sites}
                onChange={(event, newValue) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    Sites: newValue.map((site) => ({
                      SiteID: site.SiteID,
                      SiteName: site.SiteName,
                    })),
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sites"
                    placeholder="Select sites..."
                    margin="normal"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
